import React from 'react';
import { observer } from 'mobx-react';
import { Box, Fade } from '@material-ui/core';
import { useReportContext } from 'contexts/report/ReportContext';
import { FsComponentSize, TransactionType } from 'globals/types';
import reportLocales from 'globals/locales/report';
import {
  getCombinedStatus,
  getTransactionTypeOptions,
  isSuccessState,
  wait,
} from 'globals/utils';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import FsExpenditureTable from 'ui-elements/tables/FsExpenditureTable';
import FsTransactionRowsWithBarGraph from 'ui-elements/tables/FsTransactionRowsWithBarGraph';
import FsInnerTabPanel from 'ui-elements/tabs/FsInnerTabPanel';
import FsTransactionsTable from 'ui-elements/tables/FsTransactionsTable';
import FsPagination from 'ui-elements/tabs/FsPagination';
import FsMinWidthScrollBox from 'ui-elements/boxes/FsMinWidthScrollBox';
import {
  ContainerBox,
  PaginationWrapperBox,
} from './AccountTransactionsSection.style-elements';
import {
  getCategories,
  getRecurringTransactions,
  getTransactions,
} from './AccountTransactionsSection.utils';
import Filters from './Filters';

// Locales
const { tabLabels, title } = reportLocales.account.transactionsSection;

const AccountTransactionsSection: React.FC = () => {
  const {
    report: {
      transactions: {
        status: transactionsStatus,
        getTransactionArray,
        getPaginationInfo,
      },
      bankAccountsFinancialStats: {
        status: categoriesStatus,
        expenditureCategories,
        incomeCategories,
      },
      recurringExpenditures: {
        status: recurringExpendituresStatus,
        rows: expendituresRows,
      },
      recurringIncomes: { status: recurringIncomesStatus, rows: incomesRows },
    },
  } = useReportContext();
  const combinedStatus = getCombinedStatus([
    categoriesStatus,
    recurringExpendituresStatus,
    recurringIncomesStatus,
    transactionsStatus,
  ]);
  const [tabIdx, setTabIdx] = React.useState<number>(0);
  const [type, setType] = React.useState<string>(
    getTransactionTypeOptions()[0].value
  );
  const [searchPhrase, setSearchPhrase] = React.useState<string>('');
  const [blockIdx, setBlockIdx] = React.useState<number>(tabIdx);
  const [isPending, setIsPending] = React.useState<boolean>(false);
  const transactions = getTransactions(getTransactionArray, searchPhrase, type);
  const recurringTransactions = getRecurringTransactions(
    incomesRows,
    expendituresRows,
    type
  );
  const categories = getCategories(
    incomeCategories,
    expenditureCategories,
    type
  );

  const handleTabChange = async (
    event: React.ChangeEvent<Record<string, unknown>>,
    newTabIdx: number
  ): Promise<void> => {
    setTabIdx(newTabIdx);
    setIsPending(true);
    setSearchPhrase('');
    await wait(300);
    setBlockIdx(newTabIdx);
    setIsPending(false);
  };

  const isBlockIdx = (idx: number): boolean => {
    return idx === blockIdx;
  };

  if (!isSuccessState(combinedStatus))
    return (
      <FsDefaultFallbackComponent
        status={combinedStatus}
        data-test="default-fallback-component"
        withShadow={false}
      />
    );

  return (
    <ContainerBox>
      <FsPaper
        title={title}
        paddingSize={FsComponentSize.XS}
        withShadow={false}
        data-test="report-account-expenditure-section"
      >
        <FsInnerTabPanel
          labels={tabLabels}
          onChange={handleTabChange}
          value={tabIdx}
          variant="scrollable"
        />
        <Filters
          showSearch={!isPending && isBlockIdx(0)}
          type={type}
          searchPhrase={searchPhrase}
          onSearchPhraseChange={setSearchPhrase}
          onTypeChange={setType}
        />
        <Fade in={!isPending} timeout={200}>
          <Box>
            {isBlockIdx(0) && (
              <>
                <FsMinWidthScrollBox breakpointDown="md" minWidth={600}>
                  <FsTransactionsTable transactions={transactions} />
                </FsMinWidthScrollBox>
                <FsMinWidthScrollBox minWidth={290}>
                  <PaginationWrapperBox>
                    <FsPagination paginationInfo={getPaginationInfo()} />
                  </PaginationWrapperBox>
                </FsMinWidthScrollBox>
              </>
            )}
            {isBlockIdx(1) && (
              <FsTransactionRowsWithBarGraph
                data-test="transaction-rows"
                rows={recurringTransactions}
                type={TransactionType.EXPENDITURE}
              />
            )}
            {isBlockIdx(2) && (
              <FsExpenditureTable
                data-test="expenditure-table"
                rows={categories}
                transactionType={TransactionType.EXPENDITURE}
              />
            )}
          </Box>
        </Fade>
      </FsPaper>
    </ContainerBox>
  );
};

export default observer(AccountTransactionsSection);
